/**
 * VFS / KB URI rewriting
 *
 * Agents reference VFS files in AUI surface props using
 * `vfs:///{path}{?v=...}` (current surface owner) or
 * `vfs://{ownerId}/{path}{?v=...}` (absolute — any host: `con-…`,
 * `kb-…`, `skl-…`). The frontend rewrites both forms to absolute API
 * URLs under the unified `/api/v1/vfs/{ownerId}/...` route so that
 * `<img src>`, markdown images, and `<a href>` load with the existing
 * auth cookie. The same rewrite is applied to "*Url" content props
 * (e.g. `MarkdownEditor.contentUrl`) which are then fetched at render
 * time via `useVfsContent`.
 *
 * Knowledge-base files use a separate `kb://{kbId}/{path}{?query}`
 * scheme because they're served from a different endpoint with a
 * different (JSON-wrapped) shape: `/api/v1/kb/{kbId}/content/{path}`.
 * `kb://` is rewritten to that content URL; `useVfsContent` recognises
 * the route and unwraps the envelope. Unlike `vfs://` there is no
 * relative form — a KB id is always required. Note: `kb://` only works
 * for "*Url"/content props fetched via `useVfsContent`; it is NOT a
 * direct-serve URL for `<img src>`/`<a href>` (that endpoint returns
 * JSON, not raw bytes).
 *
 * `baseUrl` should be the API origin (e.g. `https://api.example.com`) in
 * production and an empty string in development (where the Vite proxy
 * forwards `/api/v1/...` to staging).
 */

import type { AuiNode } from '../types/canvases';

// vfs://[authority]/path[?query]
//   authority empty (vfs:///...) → resolve against the surface's owner
//   authority present (vfs://{ownerId}/...) → owner baked in (delegated
//     runs, cross-host references)
const VFS_URI_RE = /vfs:\/\/([^/?#\s)]*)\/([^?#\s)]+)(\?[^#\s)]*)?/g;

// kb://{kbId}/path[?query] — authority (the `kbs-…` id) is required.
const KB_URI_RE = /kb:\/\/([^/?#\s)]+)\/([^?#\s)]+)(\?[^#\s)]*)?/g;

/**
 * Rewrite every `vfs://[authority]/{path}{?query}` occurrence in a string
 * to an absolute API URL.
 *
 * @param currentOwnerId  The owner of the surrounding surface — used to
 *   resolve bare `vfs:///` URIs. For a conversation surface this is the
 *   conversation ID (`con-…`); for KB/skill surfaces it is the KB/skill
 *   ID. Absolute URIs ignore this argument and use their embedded owner.
 */
export function rewriteVfsUrisInString(
  value: string,
  currentOwnerId: string,
  baseUrl = '',
): string {
  if (!value.includes('vfs://') && !value.includes('kb://')) return value;
  let out = value.replace(VFS_URI_RE, (_, authority, path, query) => {
    const owner = authority || currentOwnerId;
    return `${baseUrl}/api/v1/vfs/${owner}/${path}${query ?? ''}`;
  });
  // KB content URL. The KB endpoint encodes the whole path as a single
  // segment (`encodeURIComponent`, slashes → %2F), matching
  // `kbApi.readText`'s `encodePath`.
  out = out.replace(KB_URI_RE, (_, kbId, path, query) => {
    return `${baseUrl}/api/v1/kb/${kbId}/content/${encodeURIComponent(path)}${query ?? ''}`;
  });
  return out;
}

/**
 * Return a new node with `vfs:///` URIs rewritten in any top-level string prop.
 *
 * Exception: `MarkdownEditor.content` is left untouched so the renderer can
 * detect a bare `vfs:///` URI and fetch it via `useVfsContent` (instead of the
 * URL appearing as literal markdown).
 */
export function rewriteVfsUrisInNode(
  node: AuiNode,
  currentOwnerId: string,
  baseUrl = '',
): AuiNode {
  const props = node.component.props;
  if (!props) return node;

  const isMarkdownEditor = node.component.type === 'MarkdownEditor';

  let changed = false;
  const nextProps: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(props)) {
    const skip = isMarkdownEditor && key === 'content';
    if (
      !skip &&
      typeof val === 'string' &&
      (val.includes('vfs://') || val.includes('kb://'))
    ) {
      const next = rewriteVfsUrisInString(val, currentOwnerId, baseUrl);
      if (next !== val) changed = true;
      nextProps[key] = next;
    } else {
      nextProps[key] = val;
    }
  }
  if (!changed) return node;
  return { ...node, component: { ...node.component, props: nextProps } };
}
